import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { getSignUpStatus } from '@/lib/api';
import {
  ButtonLink,
  DiffList,
  IconArrowRight,
  IconCheck,
  ProgressIndicator,
  Skeleton,
  type DiffRow,
} from '@/components/carbon';
import { FrameNote, PUBLIC_LINK, SignInFrame } from '@/components/PublicLayout';

export interface SignUpStatusInfo {
  sessionId: string;
  status: string;
  tenantId?: string;
  tenantName?: string;
  createdAt: string;
  completedAt?: string;
}

const SignUpSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [statusInfo, setStatusInfo] = useState<SignUpStatusInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let ignore = false;

    const fetchStatus = async () => {
      // Get sessionId from URL or localStorage
      const sessionId = searchParams.get('session') || localStorage.getItem('guardian_signup_session');

      if (sessionId) {
        setIsLoading(true);
        try {
          const status = await getSignUpStatus(sessionId);
          if (ignore) return;
          setStatusInfo(status);
          // Clean up localStorage after successfully fetching status
          localStorage.removeItem('guardian_signup_session');
        } catch {
          // Non-critical - we can still show success state
        } finally {
          if (!ignore) {
            setIsLoading(false);
          }
        }
      }
    };

    fetchStatus();

    return () => {
      ignore = true;
    };
  }, [searchParams]);

  return <SignUpSuccessView statusInfo={statusInfo} isLoading={isLoading} />;
};

function formatDate(dateString: string): string {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateString;
  }
}

/** A pass state: neutral text led by the check glyph (D12), never green. */
function Granted({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-2 text-body text-text-primary">
      <IconCheck />
      {children}
    </span>
  );
}

/**
 * The next steps after consent, as a vertical progress indicator: consent is
 * done, signing in is the current step. The approval between the two is
 * iuvo's, so it is not a step of the customer's.
 */
export function NextSteps() {
  return (
    <section aria-labelledby="next-steps-heading" className="mt-8">
      <h2 id="next-steps-heading" className="m-0 mb-4 text-body font-semibold text-text-primary">
        What happens next
      </h2>
      <ProgressIndicator
        vertical
        aria-label="Setup steps"
        steps={[
          { id: 'consent', label: 'Grant admin consent', state: 'complete' },
          {
            id: 'sign-in',
            label: 'Sign in to Guardian',
            state: 'current',
            helper: 'Use your Microsoft work account. Guardian opens the moment your access is ready.',
          },
          {
            id: 'connect',
            label: 'Connect your tenant',
            state: 'incomplete',
            helper:
              'Guardian gives you a short setup script and walks your Microsoft 365 admin through running it, about ten minutes. Apart from turning on Exchange organization customization if it’s off, and removing the Compliance Administrator role from Microsoft’s M365 Tenant Configuration Manager app unless your setup includes Purview compliance monitoring, the script doesn’t change your policies or settings.',
          },
          {
            id: 'watch',
            label: 'Watch starts on its own',
            state: 'incomplete',
            helper:
              'Once the connection checks out, Guardian sizes your coverage and switches on continuous watch. Nothing more is needed from you.',
          },
        ]}
      />
    </section>
  );
}

export interface SignUpSuccessViewProps {
  statusInfo: SignUpStatusInfo | null;
  isLoading: boolean;
}

/** The success page body (mockup carbon/04 frame). Pure, for SSR render tests. */
export function SignUpSuccessView({ statusInfo, isLoading }: SignUpSuccessViewProps) {
  const rows: DiffRow[] = [];
  if (statusInfo?.tenantName) rows.push({ label: 'Organization', value: <span className="text-body text-text-primary">{statusInfo.tenantName}</span> });
  rows.push({
    label: 'Status',
    value: <Granted>{!statusInfo || statusInfo.status === 'Completed' ? 'Consent granted' : statusInfo.status}</Granted>,
  });
  if (statusInfo?.completedAt) rows.push({ label: 'Completed', value: <span className="text-body text-text-primary">{formatDate(statusInfo.completedAt)}</span> });

  return (
    <SignInFrame
      brandTo="/signup"
      headline="Sign in to connect your tenant."
      lede="Consent is granted, and setup continues inside Guardian."
    >
      {isLoading ? <Skeleton lines={2} label="Loading your sign-up details" /> : <DiffList rows={rows} />}

      <NextSteps />

      <div className="mt-8">
        <ButtonLink to="/" kind="primary" icon={<IconArrowRight />}>
          Continue to Guardian
        </ButtonLink>
      </div>

      <FrameNote>
        <p className="m-0">
          Want to know what Guardian reads?{' '}
          <Link to="/docs" className={PUBLIC_LINK}>
            Read the documentation
          </Link>{' '}
          or{' '}
          <Link to="/docs/permissions" className={PUBLIC_LINK}>
            review the permissions
          </Link>
          .
        </p>
        <p className="m-0">
          Questions?{' '}
          <a href="mailto:support@iuvotech.com" className={PUBLIC_LINK}>
            support@iuvotech.com
          </a>
        </p>
      </FrameNote>
    </SignInFrame>
  );
}

export default SignUpSuccess;
